import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Upload, 
  Plus, 
  Search, 
  Trash2,
  Calendar,
  FileSpreadsheet,
  AlertTriangle,
  RotateCcw,
  ChevronDown,
  CheckCircle,
  Clock,
  Link2
} from "lucide-react";
import * as XLSX from "xlsx";

export default function Scadenze() {
  const [scadenze, setScadenze] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStato, setFilterStato] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [selectedScadenza, setSelectedScadenza] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [formData, setFormData] = useState({
    cliente_id: "",
    cliente_nominativo: "",
    importo: "",
    data_scadenza: "",
    data_incasso: "",
    numero_fattura: "",
    data_fattura: "",
    descrizione: ""
  });
  const fileInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const [scadenzeRes, clientiRes] = await Promise.all([
        axios.get(`${API}/scadenze`),
        axios.get(`${API}/clienti`)
      ]);
      setScadenze(scadenzeRes.data);
      setClienti(clientiRes.data);
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      toast.loading("Importazione in corso...");
      const res = await axios.post(`${API}/scadenze/import`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.dismiss();
      toast.success(`Importate ${res.data.imported} scadenze`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} errori durante l'importazione`);
      }
      fetchData();
    } catch (e) {
      toast.dismiss();
      toast.error("Errore durante l'importazione");
    }
    e.target.value = "";
  };

  const handleAdd = async () => {
    const cliente = clienti.find(c => c.id === formData.cliente_id);
    const data = {
      ...formData,
      cliente_nominativo: cliente?.nominativo || formData.cliente_nominativo,
      importo: parseFloat(formData.importo) || 0
    };

    try {
      await axios.post(`${API}/scadenze`, data);
      toast.success("Scadenza aggiunta");
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error("Errore nell'aggiunta della scadenza");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/scadenze/${selectedScadenza.id}`);
      toast.success("Scadenza eliminata");
      setShowDeleteDialog(false);
      setSelectedScadenza(null);
      fetchData();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(`${API}/scadenze/clear/all`);
      toast.success("Tutte le scadenze eliminate");
      setShowClearDialog(false);
      fetchData();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleBulkUpdateStato = async (nuovoStato) => {
    if (selectedIds.length === 0) {
      toast.error("Seleziona almeno una scadenza");
      return;
    }
    try {
      const res = await axios.post(
        `${API}/scadenze/bulk-update-stato?nuovo_stato=${nuovoStato}`,
        selectedIds
      );
      toast.success(`${res.data.updated} scadenze aggiornate a "${nuovoStato === 'pending' ? 'In attesa' : 'Elaborata'}"`);
      setSelectedIds([]);
      fetchData();
    } catch (e) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleResetAllStato = async () => {
    try {
      const res = await axios.post(`${API}/scadenze/reset-all-stato`);
      toast.success(`${res.data.updated} scadenze riportate a "In attesa"`);
      fetchData();
    } catch (e) {
      toast.error("Errore nel reset");
    }
  };

  const handleAutoMatchClienti = async () => {
    try {
      toast.loading("Abbinamento automatico in corso...");
      const res = await axios.post(`${API}/scadenze/auto-match-clienti`);
      toast.dismiss();
      
      if (res.data.matched > 0) {
        toast.success(`${res.data.matched} scadenze abbinate automaticamente!`);
      }
      if (res.data.unmatched_count > 0) {
        toast.warning(`${res.data.unmatched_count} scadenze non abbinate (nome cliente non trovato)`);
      }
      fetchData();
    } catch (e) {
      toast.dismiss();
      toast.error("Errore nell'abbinamento");
    }
  };

  const handleManualMatch = async (scadenzaId, clienteId) => {
    try {
      await axios.put(`${API}/scadenze/${scadenzaId}/match-cliente?cliente_id=${clienteId}`);
      toast.success("Abbinamento salvato");
      fetchData();
    } catch (e) {
      toast.error("Errore nell'abbinamento");
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: "",
      cliente_nominativo: "",
      importo: "",
      data_scadenza: "",
      data_incasso: "",
      numero_fattura: "",
      data_fattura: "",
      descrizione: ""
    });
    setSelectedScadenza(null);
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredScadenze.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredScadenze.map(s => s.id));
    }
  };

  const handleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const filteredScadenze = scadenze.filter(s => {
    const matchSearch = s.cliente_nominativo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.numero_fattura?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStato = filterStato === "all" || s.stato === filterStato;
    return matchSearch && matchStato;
  });

  const totalePending = scadenze
    .filter(s => s.stato === "pending")
    .reduce((sum, s) => sum + s.importo, 0);

  const totaleVisualizzato = filteredScadenze.reduce((sum, s) => sum + s.importo, 0);

  const totaleSelezionato = scadenze
    .filter(s => selectedIds.includes(s.id))
    .reduce((sum, s) => sum + s.importo, 0);

  const downloadTemplate = () => {
    const template = [
      {
        "Cliente": "Mario Rossi SRL",
        "Importo": 1500.50,
        "Data scadenza": "2024-03-15",
        "Data incasso": "2024-03-15",
        "Numero": "FT-001/2024",
        "Data": "2024-03-01"
      }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Scadenze");
    XLSX.writeFile(wb, "template_scadenze.xlsx");
  };

  const getStatusBadge = (stato) => {
    switch (stato) {
      case "pending":
        return <Badge variant="outline" className="status-pending">In attesa</Badge>;
      case "processed":
        return <Badge variant="outline" className="status-processed">Elaborata</Badge>;
      default:
        return <Badge variant="outline">{stato}</Badge>;
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="scadenze-title">
            Scadenze Incassi
          </h1>
          <p className="text-slate-500 mt-1">
            Totale in attesa: <span className="font-semibold text-slate-900">
              € {totalePending.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={downloadTemplate}
            data-testid="download-template-btn"
            className="gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Template
          </Button>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            data-testid="import-scadenze-btn"
            className="gap-2"
          >
            <Upload className="w-4 h-4" />
            Importa Excel
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button 
            onClick={() => setShowAddDialog(true)}
            data-testid="add-scadenza-btn"
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Nuova Scadenza
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Lista Scadenze ({filteredScadenze.length})
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-scadenze"
                />
              </div>
              <Select value={filterStato} onValueChange={setFilterStato}>
                <SelectTrigger className="w-40" data-testid="filter-stato">
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="pending">In attesa</SelectItem>
                  <SelectItem value="processed">Elaborati</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Azioni Massive */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="bulk-actions-btn">
                    Azioni
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={handleAutoMatchClienti}
                    data-testid="auto-match-btn"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Abbina Automatico a Clienti
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => handleBulkUpdateStato('pending')}
                    disabled={selectedIds.length === 0}
                    data-testid="bulk-set-pending"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Imposta "In attesa" ({selectedIds.length})
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleBulkUpdateStato('processed')}
                    disabled={selectedIds.length === 0}
                    data-testid="bulk-set-processed"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Imposta "Elaborata" ({selectedIds.length})
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleResetAllStato}
                    data-testid="reset-all-stato"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset TUTTE a "In attesa"
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {scadenze.length > 0 && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowClearDialog(true)}
                  className="text-rose-600 hover:text-rose-700"
                  data-testid="clear-all-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
              ))}
            </div>
          ) : filteredScadenze.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessuna scadenza trovata</p>
              <p className="text-sm text-slate-400 mt-1">
                Importa un file Excel o aggiungi manualmente
              </p>
            </div>
          ) : (
            <>
              {/* Barra selezione */}
              {selectedIds.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-800">
                    {selectedIds.length} scadenze selezionate - Totale: € {totaleSelezionato.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedIds([])}
                    className="text-blue-600"
                  >
                    Deseleziona
                  </Button>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full data-table" data-testid="scadenze-table">
                  <thead>
                    <tr>
                      <th className="w-12">
                        <Checkbox 
                          checked={selectedIds.length === filteredScadenze.length && filteredScadenze.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-scadenze"
                        />
                      </th>
                      <th>Cliente</th>
                      <th>Abbinato</th>
                      <th>Fattura</th>
                      <th>Data Scadenza</th>
                      <th className="text-right">Importo</th>
                      <th>Stato</th>
                      <th className="text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredScadenze.map((scadenza) => (
                      <tr 
                        key={scadenza.id} 
                        data-testid={`scadenza-row-${scadenza.id}`}
                        className={selectedIds.includes(scadenza.id) ? 'bg-blue-50/50' : ''}
                      >
                        <td>
                          <Checkbox 
                            checked={selectedIds.includes(scadenza.id)}
                            onCheckedChange={() => handleSelect(scadenza.id)}
                          />
                        </td>
                        <td className="font-medium">{scadenza.cliente_nominativo}</td>
                        <td>
                          {scadenza.cliente_id ? (
                            <Badge variant="outline" className="status-processed text-xs">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Sì
                            </Badge>
                          ) : (
                            <Select onValueChange={(v) => handleManualMatch(scadenza.id, v)}>
                              <SelectTrigger className="h-7 w-28 text-xs">
                                <SelectValue placeholder="Abbina..." />
                              </SelectTrigger>
                              <SelectContent>
                                {clienti.map((c) => (
                                  <SelectItem key={c.id} value={c.id} className="text-xs">
                                    {c.nominativo.substring(0, 25)}...
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="font-mono text-xs">{scadenza.numero_fattura || "-"}</td>
                        <td>{scadenza.data_scadenza}</td>
                        <td className="text-right tabular-nums font-medium">
                          € {scadenza.importo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </td>
                        <td>{getStatusBadge(scadenza.stato)}</td>
                        <td className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedScadenza(scadenza);
                              setShowDeleteDialog(true);
                            }}
                            data-testid={`delete-scadenza-${scadenza.id}`}
                            className="text-rose-600 hover:text-rose-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100 font-semibold">
                      <td></td>
                      <td colSpan="4" className="text-right px-4 py-3">
                        Totale ({filteredScadenze.length} scadenze):
                      </td>
                      <td className="text-right tabular-nums px-4 py-3 text-lg">
                        € {totaleVisualizzato.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuova Scadenza</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Select 
                value={formData.cliente_id} 
                onValueChange={(v) => setFormData({ ...formData, cliente_id: v })}
              >
                <SelectTrigger data-testid="select-cliente">
                  <SelectValue placeholder="Seleziona cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clienti.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nominativo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="importo">Importo *</Label>
                <Input
                  id="importo"
                  type="number"
                  step="0.01"
                  value={formData.importo}
                  onChange={(e) => setFormData({ ...formData, importo: e.target.value })}
                  data-testid="input-importo"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="numero_fattura">Numero Fattura</Label>
                <Input
                  id="numero_fattura"
                  value={formData.numero_fattura}
                  onChange={(e) => setFormData({ ...formData, numero_fattura: e.target.value })}
                  data-testid="input-numero-fattura"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="data_scadenza">Data Scadenza *</Label>
                <Input
                  id="data_scadenza"
                  type="date"
                  value={formData.data_scadenza}
                  onChange={(e) => setFormData({ ...formData, data_scadenza: e.target.value })}
                  data-testid="input-data-scadenza"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data_incasso">Data Incasso *</Label>
                <Input
                  id="data_incasso"
                  type="date"
                  value={formData.data_incasso}
                  onChange={(e) => setFormData({ ...formData, data_incasso: e.target.value })}
                  data-testid="input-data-incasso"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="data_fattura">Data Fattura</Label>
              <Input
                id="data_fattura"
                type="date"
                value={formData.data_fattura}
                onChange={(e) => setFormData({ ...formData, data_fattura: e.target.value })}
                data-testid="input-data-fattura"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleAdd} data-testid="save-scadenza-btn">
              Salva Scadenza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina Scadenza</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questa scadenza? 
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-scadenza-btn"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear All Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Elimina Tutte le Scadenze
            </AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione eliminerà TUTTE le scadenze ({scadenze.length} record).
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClearAll}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-clear-all-btn"
            >
              Elimina Tutto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
